import * as React from 'react'
import { Copy, ExternalLink, Info, KeyRound, Loader2 } from 'lucide-react'
import { useLoginLink } from '@/hooks/use-login-link'
import { writeClipboard } from '@/components/ui/copy-button'
import { Button } from '@/components/ui/button'
import { Dropdown, type DropdownItem } from '@/components/ui/dropdown'
import { useToast } from '@/components/ui/toast'

/**
 * Hand the session this system is holding to a person.
 *
 * Two actions — put the link on the clipboard, or open it here. Both do the same
 * thing at the other end: Amazon's SPA takes the session off the `#/auth-return`
 * hash and lands signed in as the customer, on My Applications.
 *
 * ── Why the request happens on OPEN and not on click ────────────────
 * Both actions have to run inside the user's gesture. `window.open` outside one
 * is a blocked popup, and a clipboard write outside one is refused — silently,
 * which is the whole reason `writeClipboard` returns a boolean instead of
 * resolving. Awaiting a fetch inside `onSelect` spends the activation before
 * either can happen, so the URL has to be in hand BEFORE the click. Opening the
 * menu is itself a gesture and lands a moment earlier, which makes it the right
 * trigger: nothing is fetched until the operator asks for the menu, and by the
 * time they pick a row the answer is already here. In practice the request —
 * two indexed reads — finishes inside the menu's own open animation.
 *
 * Re-fetched on EVERY open rather than reused. A session expires while the
 * drawer sits there, and handing somebody a dead link that looks alive is worse
 * than a 40ms wait.
 *
 * ── This component holds a live credential ──────────────────────────
 * The URL signs the holder in as the customer until the session expires. It is
 * kept only in this component's mutation result, never in the query cache, and
 * the drawer mounts this under `key={customerId}` so it is discarded when the
 * operator moves to another customer or closes the panel. It is never logged.
 */
export function LoginLinkMenu({ customerId }: { customerId: string }) {
  const link = useLoginLink()
  const { toast } = useToast()

  /*
   * The mutation object is new on every render, so it cannot be a dependency;
   * a ref is how the drawer solves the same problem for `start`.
   */
  const linkRef = React.useRef(link)
  linkRef.current = link

  const onOpenChange = React.useCallback(
    (open: boolean) => {
      if (open) linkRef.current.mutate(customerId)
    },
    [customerId]
  )

  const url = link.data?.ok === true ? link.data.url : null

  const onCopy = React.useCallback(() => {
    if (url === null) return
    /*
     * Kicked off synchronously inside the gesture. `writeClipboard` reports
     * whether the write actually landed — on an http:// origin the async
     * clipboard API is missing entirely and the fallback can still be refused —
     * so the toast says what happened rather than assuming success.
     */
    void writeClipboard(url).then((ok) => {
      toast(
        ok
          ? {
              title: 'Login link copied',
              description: link.data?.message,
              tone: 'ok',
            }
          : {
              title: 'Could not copy the link',
              description:
                'The browser refused clipboard access. Use “Open in new tab” instead, or copy from the address bar there.',
              tone: 'warn',
            }
      )
    })
  }, [url, link.data?.message, toast])

  const onOpenTab = React.useCallback(() => {
    if (url === null) return
    // `noopener` matters more than usual here: the opened page carries a live
    // session, and it must not get a handle back to this console.
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [url])

  const items = React.useMemo<DropdownItem[]>(() => {
    /*
     * `!link.data` is the same row as `isPending`, and not redundant.
     *
     * `mutate` is dispatched from Radix's own open handler, so there is a frame
     * where the menu is mounted and the mutation has not been marked pending
     * yet. Falling through would render a menu with no rows in it — a 200px
     * empty box — which reads as a broken control rather than a fast one.
     */
    if (link.isPending || (!link.data && !link.error)) {
      return [
        {
          key: 'pending',
          label: 'Reading their session…',
          icon: <Loader2 className="animate-spin" />,
          disabled: true,
        },
      ]
    }

    if (link.error) {
      return [
        {
          key: 'error',
          label: errorMessage(link.error),
          icon: <Info />,
          disabled: true,
          wrap: true,
        },
      ]
    }

    // No session, or a market this link cannot serve. The server's sentence is
    // written to be shown verbatim and already says what to do about it.
    if (link.data && !link.data.ok) {
      return [
        { key: 'none', label: link.data.message, icon: <Info />, disabled: true, wrap: true },
      ]
    }

    // Unreachable on the server's contract (`ok` true implies a url), but the
    // alternative to a guard here is an empty menu, so it is worth the line.
    if (url === null) {
      return [
        {
          key: 'none',
          label: 'No link came back for this customer.',
          icon: <Info />,
          disabled: true,
          wrap: true,
        },
      ]
    }

    return [
      { key: 'copy', label: 'Copy link', icon: <Copy />, onSelect: onCopy },
      { key: 'open', label: 'Open in new tab', icon: <ExternalLink />, onSelect: onOpenTab },
      {
        // Who the link signs in as and how long it lasts — checked before it is
        // sent to somebody, which is the moment being wrong about it costs the
        // most. A row rather than a tooltip because it has to be readable on the
        // tablet, where there is no hover.
        key: 'about',
        label: link.data?.message ?? '',
        disabled: true,
        wrap: true,
        separatorBefore: true,
      },
    ]
  }, [link.isPending, link.error, link.data, url, onCopy, onOpenTab])

  return (
    <Dropdown
      align="end"
      label="Login link"
      // Inside the drawer's dialog — see the prop's note. Two nested modal
      // layers can leave `pointer-events: none` on `body` after this one closes.
      modal={false}
      onOpenChange={onOpenChange}
      items={items}
      trigger={
        <Button variant="secondary" size="sm">
          <KeyRound aria-hidden="true" className="size-3.5" />
          Login link
        </Button>
      }
    />
  )
}

/**
 * The server's own message, not a generic one — the same rule the drawer applies
 * to a failed Start. `ApiError` carries the API's `error.message`, which for
 * this endpoint is the entire diagnostic ("This customer has no email on
 * file…", "Too many requests").
 */
function errorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message
    if (typeof m === 'string' && m.length > 0) return m
  }
  return 'The server rejected the request.'
}
